/**
 * Company Fee Preview Component
 *
 * Collapsible display showing officer info, fee count, last updated,
 * and expandable fee details for a title/escrow company.
 */

import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, DollarSign, Calendar, User, Layers } from 'lucide-react';
import { TitleEscrowCompany, GroupedFeeTiers } from '@/types/title-escrow';

interface CompanyFeePreviewProps {
  company: TitleEscrowCompany;
  fees: GroupedFeeTiers[];
  isOpen: boolean;
  onToggle: () => void;
  presentation?: 'default' | 'profile';
}

/**
 * Format currency for display
 */
function formatCurrency(amount: number | null): string {
  if (amount === null) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Format loan amount range for tiered fees
 */
function formatLoanRange(min: number | null, max: number | null): string {
  const minStr = min !== null ? formatCurrency(min) : '$0';
  const maxStr = max !== null ? formatCurrency(max) : 'No limit';
  return `${minStr} - ${maxStr}`;
}

export const CompanyFeePreview: React.FC<CompanyFeePreviewProps> = ({
  company,
  fees,
  isOpen,
  onToggle,
  presentation = 'default'
}) => {
  // Calculate last updated from max of all fee updated_at timestamps
  const lastUpdated = fees.length > 0
    ? new Date(
        Math.max(
          ...fees.flatMap((f) =>
            f.tiers.map((t) => new Date(t.updatedAt || 0).getTime())
          )
        )
      )
    : null;

  // Format officer name
  const officerName =
    [company.officerFirstName, company.officerLastName]
      .filter(Boolean)
      .join(' ') || 'No officer assigned';

  // Check if lastUpdated is valid (not epoch)
  const hasValidDate = lastUpdated && lastUpdated.getTime() > 0;
  const isProfilePresentation = presentation === 'profile';

  return (
    <div className={cn(
      'overflow-hidden border',
      isProfilePresentation
        ? 'company-fee-preview--profile rounded-[10px]'
        : 'rounded-lg bg-muted/30'
    )}>
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger className={cn(
          'flex w-full items-center justify-between transition-colors hover:bg-muted/50',
          isProfilePresentation ? 'company-fee-preview__trigger gap-3 px-3 py-2' : 'p-3'
        )}>
          {isProfilePresentation ? (
            <>
              <span className="company-fee-preview__label text-[11px] font-bold uppercase tracking-[0.08em]">
                Fee preview
              </span>
              <span className="company-fee-preview__summary ml-auto min-w-0 truncate text-xs font-semibold">
                {officerName} · {fees.length} fee{fees.length !== 1 ? 's' : ''}
                {hasValidDate && ` · Updated ${formatDistanceToNow(lastUpdated, { addSuffix: true })}`}
              </span>
              <ChevronDown
                className={cn(
                  'company-fee-preview__chevron h-4 w-4 shrink-0 transition-transform duration-200',
                  isOpen && 'rotate-180'
                )}
              />
            </>
          ) : (
            <>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{officerName}</span>
                </div>
                {company.officerEmail && (
                  <span className="text-muted-foreground hidden sm:inline">
                    {company.officerEmail}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <DollarSign className="h-4 w-4" />
                  <span>
                    {fees.length} fee{fees.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {hasValidDate && (
                  <div className="flex items-center gap-1 text-muted-foreground hidden md:flex">
                    <Calendar className="h-4 w-4" />
                    <span>
                      Updated {formatDistanceToNow(lastUpdated, { addSuffix: true })}
                    </span>
                  </div>
                )}
                <ChevronDown
                  className={cn(
                    'h-4 w-4 text-muted-foreground transition-transform duration-200',
                    isOpen && 'rotate-180'
                  )}
                />
              </div>
            </>
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t">
            {fees.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground text-center">
                No fees configured for this company
              </div>
            ) : (
              <div className="divide-y">
                {fees.map((fee) => (
                  <div key={fee.feeId} className={isProfilePresentation ? 'px-3 py-2' : 'p-3'}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{fee.feeName}</span>
                        {fee.isConditional && (
                          <Badge
                            variant="outline"
                            className="text-xs bg-amber-50 text-amber-700 border-amber-200"
                          >
                            Conditional
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {fee.pricingType === 'tiered' ? (
                          <div className="flex items-center gap-1">
                            <Layers className="h-3.5 w-3.5" />
                            <span>{fee.tiers.length} tiers</span>
                          </div>
                        ) : fee.pricingType === 'percentage' ? (
                          <span className="company-fee-preview__amount">
                            {((fee.tiers[0]?.percentageRate || 0) * 100).toFixed(2)}%
                          </span>
                        ) : (
                          <span className="company-fee-preview__amount">
                            {formatCurrency(fee.tiers[0]?.amount || null)}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Show tier details for tiered pricing */}
                    {fee.pricingType === 'tiered' && fee.tiers.length > 1 && (
                      <div className="mt-2 ml-4 space-y-1">
                        {fee.tiers.map((tier) => (
                          <div
                            key={tier.id}
                            className="flex justify-between text-xs text-muted-foreground"
                          >
                            <span className="company-fee-preview__amount">
                              {formatLoanRange(tier.minLoanAmount, tier.maxLoanAmount)}
                            </span>
                            <span className="company-fee-preview__amount font-medium text-foreground">
                              {formatCurrency(tier.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Show condition description if applicable */}
                    {fee.conditionDescription && (
                      <p className="mt-1 text-xs text-muted-foreground italic">
                        {fee.conditionDescription}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default CompanyFeePreview;
